import _ from 'lodash';
import React from 'react';

import Button from 'components/Button';
import CustomerFilter from 'components/Filters/CustomerFilter';
import CreatedByAdminOrTeamFilter from 'components/Filters/CreatedByAdminOrTeamFilter';
import ToggleSelectFilter from 'components/Filters/ToggleSelectFilter';
import ToggleFilter from 'components/Filters/ToggleFilter';
import BookingFilter from 'components/Filters/BookingFilter';
import {
  default as DateRangeFilter,
  TopRightFixedCustomOverlay
} from 'components/Filters/DateRangeFilter';

import {
  RENT_KEY_FOR_API,
  DEPOSIT_KEY_FOR_API,
  ONE_OFF_CHARGE_KEY_FOR_API,
  BEFORE_CUT_OFF_DATE_KEY_FOR_API,
  INVOICE_STATUS_MAPPER_FOR_FILTERS,
  XERO_STATUSES
} from 'ducks/reporting';

import {
  fetchCustomerOptions as sdkFetchCustomerOptions,
  fetchAdminOptions as sdkFetchAdminAndTeamOptions,
  fetchBookingOptions as sdkFetchBookings
} from 'sdk/reporting';

import './styles.css';

const scheduleTypesData = [
  {label: 'Rent', value: RENT_KEY_FOR_API},
  {label: 'Deposit', value: DEPOSIT_KEY_FOR_API},
  {label: 'One-off charges', value: ONE_OFF_CHARGE_KEY_FOR_API}
];
const xeroStatusesData = [
  {label: 'Synced', value: XERO_STATUSES.SYNCED},
  {label: 'Failed', value: XERO_STATUSES.FAILED},
  {label: 'Before cut off date', value: BEFORE_CUT_OFF_DATE_KEY_FOR_API}
];
const bookingStatusData = [
  {label: 'Cancelled', value: 'Cancelled'},
  {label: 'Overstay', value: 'Overstay'},
  {label: 'Due out', value: 'Due out'},
  {label: 'In house', value: 'In house'},
  {label: 'Checked out', value: 'Checked out'},
  {label: 'No show', value: 'No show'},
  {label: 'Due in', value: 'Due in'},
  {label: 'Reserved', value: 'Reserved'},
  {label: 'Confirmed', value: 'Confirmed'},
  {label: 'Qualified', value: 'Qualified'}
];

interface InvoiceReportingFiltersProps {
  locationRef: string;
  setFilter: (field: string, value: any) => void;
  filters: any;
  clearFilters: () => void;
}

class InvoiceReportingFilters extends React.Component<
  InvoiceReportingFiltersProps
> {
  customerSelectElement: any = null;
  createdBySelectElement: any = null;
  bookingSelectElement: any = null;

  fetchAdminAndTeamOptions = search => {
    const {locationRef} = this.props;

    return sdkFetchAdminAndTeamOptions({locationRef, search});
  };

  fetchCustomerOptions = search => {
    const {locationRef} = this.props;

    return sdkFetchCustomerOptions({locationRef, search});
  };

  fetchBookingOptions = search => {
    const {locationRef} = this.props;

    return sdkFetchBookings({locationRef, search});
  };

  clearFilters = () => {
    if (_.get(this.customerSelectElement, 'state.selectedOption')) {
      this.customerSelectElement.clearSelectedOption();
    }

    if (_.get(this.createdBySelectElement, 'state.selectedOption')) {
      this.createdBySelectElement.clearSelectedOption();
    }

    if (_.get(this.bookingSelectElement, 'state.selectedOption')) {
      this.bookingSelectElement.clearSelectedOption();
    }

    this.props.clearFilters();
  };

  onChangeCreatedBy = selectedOption => {
    const {setFilter} = this.props;

    if (_.isNull(selectedOption)) {
      setFilter('createdByTeam', null);
      setFilter('createdBy', null);
    } else if (selectedOption.object.isTeam) {
      setFilter('createdByTeam', selectedOption.value);
      setFilter('createdBy', null);
    } else {
      setFilter('createdBy', selectedOption.value);
      setFilter('createdByTeam', null);
    }
  };

  render() {
    const {filters, setFilter} = this.props;

    return (
      <div className="InvoiceReportingFilters colab-row-space-between">
        <div className="colab-sidepadding-2">
          <ToggleSelectFilter
            label="Schedule"
            value={filters.scheduleTypes}
            data={scheduleTypesData}
            classNameForTesting="schedule"
            onChange={scheduleTypes =>
              setFilter('scheduleTypes', scheduleTypes)
            }
          />
          <ToggleSelectFilter
            label="Invoice Status"
            value={filters.statuses}
            classNameForTesting="invoiceStatus"
            data={INVOICE_STATUS_MAPPER_FOR_FILTERS}
            onChange={statuses => setFilter('statuses', statuses)}
          />
          <ToggleSelectFilter
            label="Xero Sync Status"
            value={filters.xeroStatuses}
            classNameForTesting="xeroSyncStatus"
            data={xeroStatusesData}
            onChange={xeroStatuses => setFilter('xeroStatuses', xeroStatuses)}
          />
          <BookingFilter
            fetchSdk={this.fetchBookingOptions}
            bookingSelectRef={el => (this.bookingSelectElement = el)}
            onChange={this.props.setFilter}
            value={filters.booking}
          />
          <ToggleSelectFilter
            label="Booking Status"
            value={filters.bookingStatuses}
            classNameForTesting="bookingStatus"
            data={bookingStatusData}
            onChange={bookingStatuses =>
              setFilter('bookingStatuses', bookingStatuses)
            }
          />
          <CustomerFilter
            customerRef={el => (this.customerSelectElement = el)}
            fetchFunction={this.fetchCustomerOptions}
            clearOption={() => setFilter('customer', null)}
            onChange={customer => setFilter('customer', customer)}
          />
          <DateRangeFilter
            label="Due date"
            fieldName="dueDate"
            classNameForTesting="dueDate"
            start={filters.dueDateStart}
            end={filters.dueDateEnd}
            onChange={(dateField, date) => setFilter(dateField, date)}
          />
          <DateRangeFilter
            label="Issue date"
            fieldName="dateIssued"
            classNameForTesting="issuedDate"
            start={filters.dateIssuedStart}
            end={filters.dateIssuedEnd}
            overlayComponent={TopRightFixedCustomOverlay}
            onChange={(dateField, date) => setFilter(dateField, date)}
          />
          <DateRangeFilter
            label="Date created"
            fieldName="createdAt"
            classNameForTesting="createdDate"
            start={filters.createdAtStart}
            end={filters.createdAtEnd}
            overlayComponent={TopRightFixedCustomOverlay}
            onChange={(dateField, date) => setFilter(dateField, date)}
          />
          <DateRangeFilter
            label="Rent start"
            fieldName="rentStart"
            classNameForTesting="rentStartDate"
            start={filters.rentStartStart}
            end={filters.rentStartEnd}
            overlayComponent={TopRightFixedCustomOverlay}
            onChange={(dateField, date) => setFilter(dateField, date)}
          />
          <DateRangeFilter
            label="Rent end"
            fieldName="rentEnd"
            classNameForTesting="rentEndDate"
            start={filters.rentEndStart}
            end={filters.rentEndEnd}
            overlayComponent={TopRightFixedCustomOverlay}
            onChange={(dateField, date) => setFilter(dateField, date)}
          />
          <CreatedByAdminOrTeamFilter
            createdByRef={el => (this.createdBySelectElement = el)}
            fetchFunction={this.fetchAdminAndTeamOptions}
            clearOption={() => setFilter('createdBy', null)}
            onChange={this.onChangeCreatedBy}
          />
          <ToggleFilter
            label="GoCardless chargeable"
            value={filters.goCardlessChargeable}
            onChange={() =>
              setFilter('goCardlessChargeable', !filters.goCardlessChargeable)
            }
            classNameForTesting="GoCardlessChargeable"
          />
        </div>
        <div>
          <Button clear onClick={this.clearFilters}>
            Clear all filters
          </Button>
        </div>
      </div>
    );
  }
}

export default InvoiceReportingFilters;
